import React from "react";
var __jsx = React.createElement;
import { UnstyledButton } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBold, faEraser, faItalic, faMinus, faPlus, faRotateLeft, faRotateRight, faUnderline } from "@fortawesome/pro-regular-svg-icons";
import { FORMAT_TEXT_COMMAND, REDO_COMMAND, UNDO_COMMAND } from "lexical";
import { useTranslation } from "react-i18next";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { ToolbarButton } from "./toolbar-controls";

/** Undo, redo, and clear-formatting buttons. */
export function HistoryButtons(_ref) {
  var editor = _ref.editor,
    onClear = _ref.onClear;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  return /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-0.5"
  }, /*#__PURE__*/React.createElement(ToolbarButton, {
    icon: faRotateLeft,
    label: t("undo", {
      defaultValue: "Undo"
    }),
    testId: "letter-undo",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.LETTER_UNDO);
      editor.dispatchCommand(UNDO_COMMAND, undefined);
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    icon: faRotateRight,
    label: t("redo", {
      defaultValue: "Redo"
    }),
    testId: "letter-redo",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.LETTER_REDO);
      editor.dispatchCommand(REDO_COMMAND, undefined);
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    icon: faEraser,
    label: t("clear_formatting", {
      defaultValue: "Clear formatting"
    }),
    testId: "letter-clear",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.LETTER_CLEAR);
      onClear();
    }
  }));
}

/** Bold, italic, and underline buttons. */
export function InlineFormatButtons(_ref2) {
  var editor = _ref2.editor;
  var _useTranslation2 = useTranslation(),
    t = _useTranslation2.t;
  return /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-0.5"
  }, /*#__PURE__*/React.createElement(ToolbarButton, {
    icon: faBold,
    label: t("bold", {
      defaultValue: "Bold"
    }),
    testId: "letter-bold",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.LETTER_BOLD);
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    icon: faItalic,
    label: t("italic", {
      defaultValue: "Italic"
    }),
    testId: "letter-italic",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.LETTER_ITALIC);
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    icon: faUnderline,
    label: t("underline", {
      defaultValue: "Underline"
    }),
    testId: "letter-underline",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.LETTER_UNDERLINE);
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
    }
  }));
}

/** Font-size stepper control (− value +). */
export function FontSizeStepper(_ref3) {
  var value = _ref3.value,
    onChange = _ref3.onChange;
  var _useTranslation3 = useTranslation(),
    t = _useTranslation3.t;
  return /*#__PURE__*/React.createElement("div", {
    className: "flex h-[36px] flex-shrink-0 items-center gap-0.5 rounded-sds-200 border border-sds-base-default px-0.5"
  }, /*#__PURE__*/React.createElement(UnstyledButton, {
    type: "button",
    "aria-label": t("decrease_font", {
      defaultValue: "Decrease font size"
    }),
    title: t("decrease_font", {
      defaultValue: "Decrease font size"
    }),
    "data-testid": "letter-font-dec",
    onMouseDown: function onMouseDown(event) {
      return event.preventDefault();
    },
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.LETTER_FONT_DEC);
      onChange(-1);
    },
    className: "flex h-7 w-7 items-center justify-center rounded-sds-200 text-sds-base-secondary transition-colors hover:text-sds-base-default"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faMinus,
    className: "h-3 w-3"
  })), /*#__PURE__*/React.createElement("span", {
    className: "w-6 text-center text-style-body-small-strong text-sds-base-default"
  }, value), /*#__PURE__*/React.createElement(UnstyledButton, {
    type: "button",
    "aria-label": t("increase_font", {
      defaultValue: "Increase font size"
    }),
    title: t("increase_font", {
      defaultValue: "Increase font size"
    }),
    "data-testid": "letter-font-inc",
    onMouseDown: function onMouseDown(event) {
      return event.preventDefault();
    },
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.LETTER_FONT_INC);
      onChange(1);
    },
    className: "flex h-7 w-7 items-center justify-center rounded-sds-200 text-sds-base-secondary transition-colors hover:text-sds-base-default"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faPlus,
    className: "h-3 w-3"
  })));
}